import { useState, useEffect } from 'react';
import { getAnalyticsSiteCollections, getAnalyticsOrganizations } from '../../../services/analytics.service';

export const EMPTY_SITE_COLLECTIONS_MESSAGE = 'No site collections found';

export const MAX_ANALYTICS_WINDOW_DAYS = 30;

function getSevenDaysAgoIsoDate() {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - 7);
  return date.toISOString().split('T')[0];
}

export const DEFAULT_ANALYTICS_START_DATE = getSevenDaysAgoIsoDate();

export function getTodayIsoDate() {
  return new Date().toISOString().split('T')[0];
}

export function buildUtcDayRange(startDate, endDate) {
  return {
    startDate: `${startDate}T00:00:00Z`,
    endDate: `${endDate}T23:59:59Z`
  };
}

/**
 * Formats an ISO date string in UTC as DD-MM-YYYY to eliminate timezone rollover.
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted DD-MM-YYYY string
 */
export function formatUtcDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${day}-${month}-${year}`;
}

/**
 * Formats a start and end date range into a consistent UTC display string (DD-MM-YYYY — DD-MM-YYYY).
 * @param {string} windowStart - UTC start date
 * @param {string} windowEnd - UTC end date
 * @returns {string} e.g. "06-09-2026 — 13-09-2026"
 */
export function formatUtcDateRange(windowStart, windowEnd) {
  if (!windowStart || !windowEnd) return 'Current Measurement Window';
  const startStr = formatUtcDate(windowStart);
  const endStr = formatUtcDate(windowEnd);
  if (!startStr || !endStr) return 'Current Measurement Window';
  return `${startStr} — ${endStr}`;
}

const SHORT_MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Formats a start and end ISO date range into a concise human-readable window (e.g. "Sep 17–24, 2026").
 * @param {string} startIso - ISO start date string
 * @param {string} endIso - ISO end date string
 * @returns {string} e.g. "Sep 17–24, 2026"
 */
export function formatHumanDateWindow(startIso, endIso) {
  if (!startIso || !endIso) return 'Current Window';
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Current Window';

  const startMonth = SHORT_MONTH_NAMES[start.getUTCMonth()];
  const endMonth = SHORT_MONTH_NAMES[end.getUTCMonth()];
  const startDay = start.getUTCDate();
  const endDay = end.getUTCDate();
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}–${endDay}, ${startYear}`;
    }
    return `${startMonth} ${startDay}–${endMonth} ${endDay}, ${startYear}`;
  }
  return `${startMonth} ${startDay}, ${startYear}–${endMonth} ${endDay}, ${endYear}`;
}

export const DATE_PRESETS = [
  { id: 'today', label: 'Today', days: 0 },
  { id: '3d', label: '3D', days: 3 },
  { id: '7d', label: '7D', days: 7 },
  { id: '30d', label: '30D', days: 30 },
  { id: 'custom', label: 'Custom', days: null }
];

export function getPresetDateRange(presetId) {
  const today = new Date();
  const todayIso = today.toISOString().split('T')[0];

  if (presetId === 'today') {
    return { startDate: todayIso, endDate: todayIso };
  }

  const preset = DATE_PRESETS.find((p) => p.id === presetId);
  if (preset && typeof preset.days === 'number') {
    const pastDate = new Date();
    pastDate.setUTCDate(pastDate.getUTCDate() - preset.days);
    return {
      startDate: pastDate.toISOString().split('T')[0],
      endDate: todayIso
    };
  }

  return null;
}

export function isDateRangeValid(startDate, endDate) {
  if (!startDate || !endDate) return false;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (isNaN(start) || isNaN(end)) return false;
  if (start > end) return false;
  const diffDays = (end - start) / (1000 * 60 * 60 * 24);
  return diffDays <= MAX_ANALYTICS_WINDOW_DAYS + 0.05;
}

export function formatDurationSeconds(totalSeconds) {
  const sec = Number(totalSeconds);
  if (!sec || isNaN(sec) || sec <= 0) return '0s';
  const roundSec = Math.round(sec);
  if (roundSec < 60) return `${roundSec}s`;
  const minutes = Math.floor(roundSec / 60);
  const remSec = roundSec % 60;
  if (roundSec < 3600) {
    return remSec > 0 ? `${minutes}m ${remSec}s` : `${minutes}m`;
  }
  const hours = Math.floor(roundSec / 3600);
  const remMin = Math.round((roundSec % 3600) / 60);
  return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`;
}

export function formatBytes(bytes, decimals = 1) {
  const b = Number(bytes);
  if (!b || isNaN(b) || b <= 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  const idx = Math.min(i, sizes.length - 1);
  return `${parseFloat((b / Math.pow(k, idx)).toFixed(dm))} ${sizes[idx]}`;
}

const DIMENSION_DISPLAY_NAMES = {
  featureusage: 'Creator Activity',
  eventsingested: 'Player Interactions',
  storage: 'Storage Used'
};

export function formatDimensionName(dimension) {
  if (!dimension) return 'Unknown Dimension';
  const key = String(dimension).toLowerCase().trim();
  if (DIMENSION_DISPLAY_NAMES[key]) {
    return DIMENSION_DISPLAY_NAMES[key];
  }
  return String(dimension)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[_,-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatConfidenceLevel(level) {
  if (!level) return { label: 'Live Data', className: 'confidence-live' };
  const str = String(level).toLowerCase().trim();
  if (str === 'rawfallback' || str === 'live') {
    return { label: 'Live Data', className: 'confidence-live' };
  }
  if (str === 'aggregatebacked' || str === 'aggregated' || str === 'summarized') {
    return { label: 'Summarized', className: 'confidence-summarized' };
  }
  if (str === 'incomplete' || str === 'partial') {
    return { label: 'Partial Data', className: 'confidence-partial' };
  }
  return { label: level, className: 'confidence-default' };
}

export function formatDimensionQuantity(quantity, unit, dimension) {
  const q = Number(quantity || 0);
  const u = String(unit || '').toLowerCase().trim();
  const d = String(dimension || '').toLowerCase().trim();

  if (u === 'bytes' || d === 'storage') {
    return {
      formatted: formatBytes(q),
      raw: `${q.toLocaleString()} bytes`
    };
  }

  if (d === 'featureusage') {
    return {
      formatted: `${q.toLocaleString()} actions`,
      raw: `${q.toLocaleString()} actions`
    };
  }

  if (d === 'eventsingested') {
    return {
      formatted: `${q.toLocaleString()} events`,
      raw: `${q.toLocaleString()} events`
    };
  }

  return {
    formatted: `${q.toLocaleString()} ${u || 'events'}`,
    raw: `${q.toLocaleString()} ${u || 'events'}`
  };
}

export function normalizeAnalyticsError(error, fallbackMessage) {
  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string' && error.message.trim()) return error.message;
  return fallbackMessage;
}

export async function loadAnalyticsSiteCollections({
  orgId,
  setSitesLoading,
  setSitesError,
  setSiteCollections,
  setSelectedSite,
  signal
}) {
  if (!orgId) {
    if (typeof setSiteCollections === 'function') setSiteCollections([]);
    if (typeof setSelectedSite === 'function') setSelectedSite('');
    try {
      localStorage.removeItem("selected_site_collection_id");
    } catch (e) { }
    return;
  }

  setSitesLoading(true);
  setSitesError(null);
  setSelectedSite('');
  if (typeof setSiteCollections === 'function') {
    setSiteCollections([]);
  }

  try {
    const data = await getAnalyticsSiteCollections({ organizationId: orgId, signal });
    if (signal?.aborted) return;

    let normalized = Array.isArray(data) ? data : [];

    // Defensive check: ensure site collections match the requested organization
    if (!isSystemOrgId(orgId) && normalized.some((s) => s.organizationId)) {
      normalized = normalized.filter(
        (s) => !s.organizationId || String(s.organizationId).toLowerCase() === String(orgId).toLowerCase()
      );
    }

    if (signal?.aborted) return;

    setSiteCollections(normalized);

    if (normalized.length > 0) {
      let savedSite = "";
      try {
        savedSite = localStorage.getItem("selected_site_collection_id") || "";
      } catch (e) { }

      const hasSavedSite = normalized.some(s => String(s.id) === String(savedSite));
      if (savedSite && hasSavedSite) {
        setSelectedSite(savedSite);
      } else {
        setSelectedSite(normalized[0].id);
        try {
          localStorage.setItem("selected_site_collection_id", normalized[0].id);
        } catch (e) { }
      }
    } else {
      setSelectedSite('');
      try {
        localStorage.removeItem("selected_site_collection_id");
      } catch (e) { }
      setSitesError(EMPTY_SITE_COLLECTIONS_MESSAGE);
    }
  } catch (error) {
    if (signal?.aborted || error?.name === 'AbortError') return;

    setSelectedSite('');
    try {
      localStorage.removeItem("selected_site_collection_id");
    } catch (e) { }
    setSitesError(normalizeAnalyticsError(error, 'Failed to load site collections'));
  } finally {
    if (!signal?.aborted) {
      setSitesLoading(false);
    }
  }
}

export const isSystemOrgId = (id) => {
  if (!id) return false;
  const str = String(id).trim().toLowerCase();
  return str === '00000000-0000-0000-0000-000000000001' || str === '00000000-0000-0000-0000-000000000000';
};

export function useAnalyticsOrganizations(roles, orgId) {
  const isSystemAdmin = (roles || []).some(
    (r) => typeof r === 'string' && r.toLowerCase() === 'systemadministrator'
  );
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(() => {
    try {
      const saved = localStorage.getItem('selected_organization_id');
      if (saved) return saved;
    } catch (e) {}
    return orgId || '';
  });
  const [orgsLoading, setOrgsLoading] = useState(false);

  useEffect(() => {
    if (!isSystemAdmin) {
      setSelectedOrg(orgId || '');
    }
  }, [isSystemAdmin, orgId]);

  useEffect(() => {
    if (isSystemAdmin) {
      let isMounted = true;
      const fetchOrgs = async () => {
        setOrgsLoading(true);
        try {
          const data = await getAnalyticsOrganizations();
          if (isMounted) {
            const orgList = Array.isArray(data) ? data : [];
            setOrganizations(orgList);
            if (orgList.length > 0) {
              setSelectedOrg((prevSelected) => {
                let saved = '';
                try {
                  saved = localStorage.getItem('selected_organization_id') || '';
                } catch (e) {}

                const candidate = prevSelected || saved || orgId;
                const exists = orgList.some((o) => String(o.id) === String(candidate));
                if (candidate && exists) {
                  return candidate;
                }
                const hasUserOrg = orgList.some((o) => String(o.id) === String(orgId));
                if (orgId && hasUserOrg) {
                  return orgId;
                }
                const nonSystemOrg = orgList.find(
                  (o) => o.name?.toLowerCase() !== 'system' && !isSystemOrgId(o.id)
                );
                return nonSystemOrg ? nonSystemOrg.id : orgList[0].id;
              });
            }
          }
        } catch (err) {
          console.error("Failed to load organizations:", err);
        } finally {
          if (isMounted) {
            setOrgsLoading(false);
          }
        }
      };
      fetchOrgs();
      return () => {
        isMounted = false;
      };
    }
  }, [isSystemAdmin, orgId]);

  const handleSetSelectedOrg = (val) => {
    setSelectedOrg(val);
    try {
      if (val) {
        localStorage.setItem('selected_organization_id', val);
      } else {
        localStorage.removeItem('selected_organization_id');
      }
    } catch (e) {}
  };

  return {
    isSystemAdmin,
    organizations,
    selectedOrg,
    setSelectedOrg: handleSetSelectedOrg,
    orgsLoading
  };
}

export { transformFunnelToSankey } from '../../../services/analytics.service';

